import { College, CollegePrograms } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';
import { selectIsSharedSessionActive } from '../sharedSession/sharedSession';
import type { RootState } from '../store';

export const collegeApi = createApi({
  reducerPath: 'collegeApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getCollegeById: build.query<College, string>({
      queryFn: async (id, queryApi, _extraOptions, baseQuery): Promise<{ data: College }> => {
        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/colleges/${id}`
          : `colleges/${id}`;

        return baseQuery({ url }) as Promise<{ data: College }>;
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getCollegeByName: build.query<College, string>({
      query: (name) => `colleges/name/${name}`,
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getCollegeByInstitutionId: build.query<College, string>({
      queryFn: async (id, queryApi, _extraOptions, baseQuery): Promise<{ data: College }> => {
        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/colleges/institution/${id}`
          : `colleges/institution/${id}`;

        return baseQuery({ url }) as Promise<{ data: College }>;
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getCollegePrograms: build.query<CollegePrograms, string>({
      queryFn: async (
        id,
        queryApi,
        _extraOptions,
        baseQuery,
      ): Promise<{ data: CollegePrograms }> => {
        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/colleges/${id}/programs`
          : `colleges/${id}/programs`;

        const result = (await baseQuery({ url })) as { data?: CollegePrograms };

        return result.data
          ? { ...result, data: normalizeCollegePrograms(result.data) }
          : (result as { data: CollegePrograms });
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
  }),
});

// Scorecard CIP titles end in a period ("Biology, General.").
const normalizeCollegePrograms = (data: CollegePrograms): CollegePrograms => ({
  ...data,
  programs: data.programs.map((program) => ({
    ...program,
    cip_desc: program.cip_desc.replace(/\.$/, ''),
  })),
});

export const {
  useGetCollegeByIdQuery,
  useGetCollegeByNameQuery,
  useGetCollegeByInstitutionIdQuery,
  useGetCollegeProgramsQuery,
  usePrefetch: usePrefetchCollege,
  util: { resetApiState },
} = collegeApi;
